import { femaleQuestionMedium, maleQuestionMedium, bulbMedium } from '@/assets/icons';
import {
  HAVING_NO_PROBLEMS,
  HAVING_NO_PROBLEMS_SOURCE,
  FORCE_STANDARDS,
  FORCE_STANDARDS_SOURCE,
  WITHOUT_DATA,
  WITHOUT_DATA_SOURCE,
} from './quotes';

const MANAGEMENT_OF_PERFORMANCE = `
<p>
  La gestión del rendimiento a través de la mejora continua debe estar centrada en el cliente. En cuanto a la creación de valor de la empresa, la necesidad del cliente debe ser la prioridad absoluta.
  El cliente puede ser la entidad a la que se vende un producto, como resultado de una transacción, pero también puede tratarse de un cliente interno, es decir, otro equipo o una entidad perteneciente a la misma empresa.<br>
  El <strong>rendimiento</strong> es la capacidad de alcanzar la satisfacción total del cliente en cuanto a la calidad de los productos de salida, de los costes y de los plazos asociados (QCD, por sus siglas en inglés) de la forma más eficaz posible.
  Para lograrlo, es preciso disponer del proceso más eficaz posible para satisfacer la necesidad del cliente.
</p>
<br>
<p>
  En la mayoría de los ámbitos existen cinco componentes estándar: S de seguridad, Q de calidad («quality»), C de costes, D de plazos («delays») y M de gestión («management») o motivación.
  La letra utilizada no tiene mucha importancia, siempre y cuando todos comprendan bien el uso de estos 5 aspectos. Ciertamente, en algunos ámbitos será necesario utilizar un sexto componente
  e incluso un séptimo, pero siempre habrá que tener cuidado de no dejarse abrumar por los indicadores.<br>
  La <strong>M</strong> de «management» es el último aspecto que hemos mencionado, en la que se suelen encontrar indicadores pobres que solo permiten elaborar informes de cuestiones como el absentismo o los retrasos de los empleados.
  Sin embargo, se trata de una clave indispensable para la mejora continua.<br>
  Es el <strong>management</strong>, o gestión, lo que va a permitirnos lograr una dinámica adecuada, una asimilación y una implicación en los procesos de mejora de la empresa.<br>
  Para distinguir una verdadera gestión del rendimiento de una simple elaboración de informes, los actores del rendimiento, es decir, quienes crean valor sobre el terreno deben hacer suyos los indicadores, y son estos mismos actores quienes deben
  definir los indicadores de gestión y los objetivos fijados para implicarse en el proceso. Esto requiere salir de un sistema piramidal, en el que los indicadores se definen desde arriba, aunque esto entrañe tomar la iniciativa y actuar con autonomía.
</p>
<div class="ia-sensei-quote">
  <span class="quote">"${FORCE_STANDARDS}"</span>
  <br>
  <span class="author">${FORCE_STANDARDS_SOURCE}</span>
</div>
<h2 class="basic-sub-title">
  La medición
</h2>
<p>
  Vamos a explicar primero el concepto de <strong>Gemba</strong>: El Gemba es el lugar donde se crea el valor. Es el lugar de trabajo sobre el terreno. En una fábrica, el Gemba es el área de producción, donde se fabrican o trabajan los productos.
  En un centro de llamadas, el Gemba es la planta o plantas donde trabaja el personal que atiende el teléfono, etc.<br>
  Ahora bien, es en el Gemba donde se mide el dato, por lo que constituye el primer punto en el orden lógico del proceso de mejora continua.<br>
  El dato es la materia prima de la mejora continua. Permite ser fáctico y evitar juicios de valor, aproximaciones o suposiciones. Además, al medir estos datos, podemos estar seguros de que las acciones adoptadas
  por las personas o los equipos poseen la dimensión correcta.
</p>
<div class="ia-sensei-quote">
  <span class="quote">"${WITHOUT_DATA}"</span>
  <br>
  <span class="author">${WITHOUT_DATA_SOURCE}</span>
</div>
<p>
  Hoy en día se toman demasiadas decisiones basándose en opiniones. Una buena gestión del rendimiento permite acabar con las suposiciones al sustituirlas por datos tangibles.
  Así, la medición permite recabar el dato y ser objetivos respecto a un proceso.
</p>

<ul>
  La medición puede ser:
  <li>en tiempo real: a través de un sistema SPC (control estadístico de procedimientos) por ejemplo, que recoja el dato en tiempo real,</li>
  <li>periódico: cada hora, se cuenta el número de piezas realizadas,</li>
  <li>por muestreo o sondeo: para cuantificar la satisfacción del cliente, por ejemplo.</li>
</ul>
<p>
  Sea cual sea el tipo de medición o su periodicidad, se va a correlacionar con un indicador preciso (o KPI, siglas de Key Performance Indicator).
  Un buen indicador es aquel relacionado de una u otra forma con la satisfacción del cliente.<br>
  Podemos utilizar dos tipos de indicadores: Los indicadores de procesos y los indicadores de resultados.
  Deben decirnos si el resultado es conforme y si se ha seguido el proceso correctamente.<br>
  Aunque generalmente en las empresas se utilizan indicadores de resultados, con frecuencia carecen de indicadores de procesos, aunque muchas veces se produzcan problemas de calidad o de incumplimiento de plazo precisamente por no haber seguido un proceso.
</p>
<div class="ia-sensei-example">
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24"><path d="${bulbMedium}"></path></svg>
  <div class="example-title">Por ejemplo:</div>
  Vamos a seguir una receta de cocina para hacer un pastel. Por supuesto, la receta nos indicará los indicadores de procesos correctos que podremos seguir, mientras que el resultado de la preparación del pastel es el pastel en sí.
  Como indicadores de procesos, podremos pues elegir la temperatura del horno, la duración del horneado, el orden de los ingredientes... y los indicadores de resultados podrán ser
  el tamaño y la consistencia del pastel, preguntar a los invitados qué les ha parecido, etc.
</div>
<div class="ia-sensei-question">
  <div class="question-decoration"></div>
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24"><path d="${femaleQuestionMedium}"></path></svg>
  <span class="question">Muy bien, entonces, ¿la medición del dato equivale a especificar los indicadores?</span>
</div>
<p>
  En parte sí, pero no se reduce a eso. La medición del dato consiste en considerar las cosas de forma fáctica, es decir, los valores de los indicadores, por supuesto, pero también todo cuanto resulte visible o verificable a través de los hechos.
  Cuando se detecta un problema, describimos lo que vemos, no hacemos suposiciones, y verificamos con datos fácticos.
  Al hacer nuestra inspección sobre el terreno, no hacemos una aproximación ni juicios de valor, sino que vemos, hablamos y analizamos.
  La medición permite analizar y desglosar los problemas.
</p>
<h2 class="basic-sub-title">
  Detectar los problemas
</h2>
<div class="ia-sensei-question">
  <div class="question-decoration"></div>
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24"><path d="${maleQuestionMedium}"></path></svg>
  <span class="question">Vaya, he vuelto a encontrar otro problema esta mañana.</span>
</div>
<p>
  ¡Pero eso es estupendo! ¡Va a tener la oportunidad de resolverlo y de mejorar! ¡Adelante!<br>
  Los problemas son las mejores oportunidades de mejora que podemos tener. Pero definamos primero qué es exactamente un problema:<br>
  Un problema es una diferencia entre la situación actual y una situación ideal.<br>
  La situación ideal puede ser un estándar o un objetivo de indicador, por ejemplo. Lógicamente, para ello es preciso que los estándares y objetivos ya estén marcados, de ahí los conceptos previos de gestión y medición.
</p>
<div class="ia-sensei-question">
  <div class="question-decoration"></div>
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24"><path d="${femaleQuestionMedium}"></path></svg>
  <span class="question">Bien, ya sé cómo encontrar los problemas. Pero ¿cómo puedo detectarlos antes, cómo puedo preverlos?</span>
</div>
<p>
  Excelente pregunta. Existen herramientas de análisis estadístico complejas, como las tarjetas de control, que permiten limitar la variabilidad de los datos. Ahora bien, este tipo de herramienta puede resultar difícil de configurar.<br>
  Sin embargo, existe una herramienta mucho más sencilla, que es además muy eficaz: la <strong>inspección sobre el terreno</strong>.
  La inspección sobre el terreno de la dirección resulta fundamental con vistas al control de los estándares por parte del Gemba y de la previsión de las diferencias.
  Pero solo es una herramienta de la dirección. Gracias a Shizen, la inspección sobre el terreno está al alcance de todos y puede configurarse, planificarse y ejecutarse en unos clics.<br>
  Incluso es posible realizar una inspección sobre el terreno entre varias personas para verificar los hechos, realizar acciones de tipo medida cautelar, identificar las causas raíz y poner en práctica y estandarizar las contramedidas para atajar los problemas y evitar a sí su reaparición.<br>
  Hoy en día, algunas empresas consolidadas que siguen un sistema de mejora continua ofrecen a los operarios realizar inspecciones sobre el terreno diarias de sus puestos de trabajo.
  Así, el personal se implica más en el proceso, adquiere autonomía e interacciona más regularmente con los demás equipos y niveles de gestión para solucionar sus problemas.
</p>
<h2 class="basic-sub-title">
  Alertar y actuar
</h2>
<p>
  Ya sabemos cómo detectar los problemas y qué podemos hacer para ir un paso por delante.
  El siguiente paso es adoptar las medidas adecuadas en función de la situación. Ese es el objetivo de contar con equipos autónomos sobre el terreno.<br>
  Sin embargo, existen determinadas situaciones y problemas que no pueden solucionarse sin ayuda o apoyo.
  En esos casos, deberemos recurrir a la persona o el equipo adecuados con el fin de atender el problema que alguien haya detectado.<br>
  Shizen permite alertar a estas personas lo antes posible para mantener la creación de valor sobre el terreno.
</p>
<div class="ia-sensei-question">
  <div class="question-decoration"></div>
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24"><path d="${maleQuestionMedium}"></path></svg>
  <span class="question">¿Cuándo deberemos pasar a la acción?</span>
</div>
<p>
  No necesariamente de forma inmediata: Ciertos problemas requerirán adoptar acciones cautelares o de emergencia.<br>
  Por ejemplo, si un tubo de descarga presenta una fuga en toda el área de producción, lógicamente habrá que encontrar una forma de evitar una inundación. Pero esta acción no forma parte de la resolución del problema.<br><br>
  Antes de solucionarlo, será necesario entender perfectamente en qué consiste y estar seguros de poder señalar al verdadero culpable.
  Ahí reside el interés de herramientas como los 5 por qué e Ishikawa, que permiten a todos identificar las causas raíz de los problemas.<br>
  Una vez que efectivamente demos con el culpable, es decir, la causa o las causas raíz, podremos determinar cuáles son las mejores acciones que adoptar para erradicar el problema.
  A la hora de definir estas acciones, es necesario asignarlas a las personas competentes y planificarlas.<br>
  Finalmente, se realizarán las acciones y se estandarizarán las soluciones para evitar que el problema vuelva a reaparecer.
</p>
<div class="ia-sensei-question">
  <div class="question-decoration"></div>
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24"><path d="${femaleQuestionMedium}"></path></svg>
  <span class="question">¿Cómo podemos entonces asegurarnos de que el problema no volverá a aparecer?</span>
</div>
<p>
  Asistiendo a las reuniones rutinarias diarias, realizando las inspecciones sobre el terreno y supervisando los indicadores:<br>
  Las rutinas son también momentos excelentes para alertar sobre los problemas y notificarlos.
  Estas prácticas diarias permiten comprobar el estado de los indicadores de rendimiento, los últimos problemas detectados y las acciones en curso.
  Una rutina debe implicar a varios equipos, en concreto el personal sobre el terreno, el personal directivo y los cargos de apoyo.
  A las rutinas de operaciones pueden unirse también los servicios de mantenimiento y de control de calidad, con el fin de favorecer un intercambio pautado y regular entre los servicios, además de garantizar una mejor comprensión de los indicadores.<br>
  El reto de las interacciones entre equipos y niveles jerárquicos es mantener una dinámica de respuesta ante los problemas. A veces hay problemas que no es posible solucionar desde el equipo que trabaja sobre el 
  terreno, por lo que será necesario recurrir a un equipo de apoyo o de dirección para su resolución. Sin interacción y sin un sistema de gestión como Shizen, el equipo no dispondrá de ningún seguimiento sobre el tratamiento y la resolución del problema.
  La falta de información generará frustración y una sensación de abandono, lo que inducirá a los empleados a dejar de comunicar sus problemas.
</p>
<div class="ia-sensei-quote">
  <span class="quote">"${HAVING_NO_PROBLEMS}"</span>
  <br>
  <span class="author">${HAVING_NO_PROBLEMS_SOURCE}</span>
</div>
<p>
  <br>
  La gestión del rendimiento consiste en una secuencia de los siguientes puntos clave: 
  medición y comprensión del dato fáctico, capacidad de analizar y detectar las diferencias
  y, finalmente, procesos adecuados para emitir alertas y actuar en el nivel adecuado.
  Shizen ofrece un sistema completo de gestión del rendimiento, que permite controlar y supervisar el rendimiento en cada nivel jerárquico.
  Neo le acompañará en cada aspecto del sistema para ayudarle u ofrecerle orientación en todos los ámbitos.
</p>
`;

const MANAGEMENT_OF_PERFORMANCE_TITLE = 'La gestión del rendimiento';

export default {
  MANAGEMENT_OF_PERFORMANCE,
  MANAGEMENT_OF_PERFORMANCE_TITLE,
};
